import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type {
  CreateCampaignInput,
  UpdateCampaignInput,
  CreateMemberInput,
  UpdateMemberInput,
  CreateCreatorInput,
} from '@/lib/api/types'
import { QUERY_KEYS } from './use-queries'
import { CREATORS_QUERY_KEYS } from './use-creators'
import { toast } from 'sonner'

/**
 * Provides a mutation hook to create a campaign with built-in cache invalidation and user feedback.
 *
 * On success, invalidates the campaigns query cache and shows a success toast. On error, shows an error toast and logs the error.
 *
 * @returns The configured mutation for creating campaigns.
 */
export function useCreateCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateCampaignInput) => api.campaigns.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.campaigns })
      toast.success('Campanha criada com sucesso!')
    },
    onError: (error) => {
      toast.error('Erro ao criar campanha')
      console.error(error)
    },
  })
}

export function useUpdateCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCampaignInput }) =>
      api.campaigns.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.campaigns })
      toast.success('Campanha atualizada com sucesso!')
    },
    onError: (error) => {
      toast.error('Erro ao atualizar campanha')
      console.error(error)
    },
  })
}

export function useForceSyncCampaign() {
  return useMutation({
    mutationFn: (id: string) => api.campaigns.forceSync(id),
    onSuccess: (_, id) => {
      toast.success(`Sincronização iniciada para campanha ${id}`)
    },
    onError: (error) => {
      toast.error('Erro ao iniciar sincronização')
      console.error(error)
    },
  })
}

export function useCreateMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateMemberInput) => api.members.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.members })
      toast.success('Membro criado com sucesso!')
    },
    onError: (error) => {
      toast.error('Erro ao criar membro')
      console.error(error)
    },
  })
}

export function useUpdateMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateMemberInput }) =>
      api.members.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.members })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.member('') })
      toast.success('Membro atualizado com sucesso!')
    },
    onError: (error) => {
      toast.error('Erro ao atualizar membro', {
        description: error.message,
      })
    },
  })
}

export function useSyncMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => api.members.sync(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.members })
      toast.success('Sincronização realizada com sucesso!')
    },
    onError: (error) => {
      toast.error('Erro ao sincronizar membro', {
        description: error.message,
      })
    },
  })
}

export function useCancelMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => api.members.cancel(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.members })
      toast.success('Membro desativado com sucesso!')
      if (result.warnings && result.warnings.length > 0) {
        result.warnings.forEach((warning: string) => {
          toast.warning(warning)
        })
      }
    },
    onError: (error) => {
      toast.error('Erro ao desativar membro', {
        description: error.message,
      })
    },
  })
}

/**
 * Provides a mutation hook to cancel a referral by id with a reason.
 *
 * Invalidates referrals and members caches on success and displays success or error toasts based on the outcome.
 *
 * @returns A React Query mutation object whose mutate function accepts `{ id, reason }` and triggers the referral cancellation; the mutation's resolved value is the API response.
 */
export function useCancelReferral() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.referrals.cancel(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.referrals })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.members })
      toast.success('Indicação cancelada com sucesso!')
    },
    onError: (error) => {
      toast.error('Erro ao cancelar indicação', {
        description: error.message,
      })
    },
  })
}

/**
 * Provides a React Query mutation for creating a creator and refreshing creators cache on success.
 *
 * The mutation calls the API to create a creator; on success it invalidates the creators list cache and shows a success toast, and on error it shows an error toast and logs the error.
 *
 * @returns A React Query mutation object that accepts a `CreateCreatorInput` and creates a creator.
 */
export function useCreateCreator() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateCreatorInput) => api.creators.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CREATORS_QUERY_KEYS.all })
      toast.success('Influencer criado com sucesso!')
    },
    onError: (error) => {
      toast.error('Erro ao criar influencer', {
        description: error.message,
      })
      console.error(error)
    },
  })
}

/**
 * Provide a mutation hook to seed default commission tiers for creators.
 *
 * On success, invalidates the commission tiers cache and shows a success toast.
 * On error, shows an error toast and logs the error to the console.
 *
 * @returns A mutation object that triggers seeding of default commission tiers when executed.
 */
export function useSeedCommissionTiers() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => api.commissionTiers.seedDefaults(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CREATORS_QUERY_KEYS.commissionTiers })
      toast.success('Tiers iniciais criados com sucesso!')
    },
    onError: (error) => {
      toast.error('Erro ao inicializar tiers de comissão', {
        description: error.message,
      })
      console.error(error)
    },
  })
}

/**
 * Provides a mutation hook to create a commission tier.
 *
 * The mutation calls the API to create a commission tier, invalidates the commission tiers cache on success, and shows success or error toasts based on outcome.
 *
 * @returns A React Query mutation configured to accept `CreateTierInput` and perform the described side effects on success or error.
 */
export function useCreateCommissionTier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: import('@/lib/api/types').CreateTierInput) =>
      api.commissionTiers.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CREATORS_QUERY_KEYS.commissionTiers })
      toast.success('Tier criado com sucesso!')
    },
    onError: (error) => {
      toast.error('Erro ao criar tier', {
        description: error.message,
      })
      console.error(error)
    },
  })
}

/**
 * Creates a React Query mutation hook to update a commission tier.
 *
 * The mutation expects an object with `id` (the tier identifier) and `data` (`UpdateTierInput`) when invoked.
 *
 * @returns A mutation configured to update a commission tier; call `mutate` with `{ id: string; data: UpdateTierInput }`.
 */
export function useUpdateCommissionTier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: import('@/lib/api/types').UpdateTierInput }) =>
      api.commissionTiers.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CREATORS_QUERY_KEYS.commissionTiers })
      toast.success('Tier atualizado com sucesso!')
    },
    onError: (error) => {
      toast.error('Erro ao atualizar tier', {
        description: error.message,
      })
      console.error(error)
    },
  })
}

/**
 * Provides a React Query mutation for creating a creator payout.
 *
 * @returns The mutation result that, on success, invalidates the `['creator-payouts']` cache and the specific creator's payouts cache (`['creators', creatorId, 'payouts']`).
 */
export function useCreateCreatorPayout() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: import('@/lib/api/types').CreatePayoutInput) =>
      api.creatorPayouts.create(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['creator-payouts'] })
      queryClient.invalidateQueries({ queryKey: ['creators', variables.creatorId, 'payouts'] })
    },
  })
}

/**
 * Provides a React Query mutation hook to update a creator payout's status.
 *
 * @returns The mutation instance that calls the API to update a payout's status and invalidates related caches: the general `creator-payouts` list, the specific payout by id, and the related creator's payouts list.
 */
export function useUpdateCreatorPayoutStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: import('@/lib/api/types').UpdatePayoutStatusInput
    }) => api.creatorPayouts.updateStatus(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['creator-payouts'] })
      queryClient.invalidateQueries({ queryKey: ['creator-payouts', data.id] })
      queryClient.invalidateQueries({ queryKey: ['creators', data.creatorId, 'payouts'] })
    },
  })
}

/**
 * Returns a mutation hook for creating a creator post that updates related caches and shows user notifications.
 *
 * @returns A React Query mutation configured to create a creator post; on success it invalidates the creator's posts and posts stats queries and shows a success toast, on error it shows an error toast with the error message.
 */
export function useCreateCreatorPost() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: import('@/lib/api/types').CreateCreatorPostInput) =>
      api.creatorPosts.create(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['creators', variables.creatorId, 'posts'] })
      queryClient.invalidateQueries({
        queryKey: ['creators', variables.creatorId, 'posts', 'stats'],
      })
      toast.success('Post registrado com sucesso!')
    },
    onError: (error) => {
      toast.error('Erro ao registrar post', { description: error.message })
    },
  })
}

/**
 * Provides a mutation hook to create a seeding for a creator.
 *
 * The mutation calls the API to create a seeding. On success it invalidates the creator's seedings cache and shows a success toast; on error it shows an error toast with the error message.
 *
 * @returns A mutation object that accepts `CreateSeedingInput` and performs the create seeding operation
 */
export function useCreateSeeding() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: import('@/lib/api/types').CreateSeedingInput) => api.seedings.create(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['creators', variables.creatorId, 'seedings'] })
      toast.success('Envio programado com sucesso!')
    },
    onError: (error) => {
      toast.error('Erro ao programar envio', { description: error.message })
    },
  })
}

/**
 * Provides a React Query mutation hook to update a seeding's status.
 *
 * Invalidates the creator's seedings list and the specific seeding cache on success, and displays success or error toasts.
 *
 * @returns The mutation object which, when called with `{ id, data }`, updates the seeding and yields the updated seeding object.
 */
export function useUpdateSeedingStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: import('@/lib/api/types').UpdateSeedingStatusInput
    }) => api.seedings.updateStatus(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['creators', data.creatorId, 'seedings'] })
      queryClient.invalidateQueries({ queryKey: ['seedings', data.id] })
      toast.success('Status do envio atualizado com sucesso!')
    },
    onError: (error) => {
      toast.error('Erro ao atualizar status', { description: error.message })
    },
  })
}
